/**
 * Portfolio module constants — enums, labels, and limits shared by the service
 * (storage boundary) and the UI. Pure data, no imports, so it can be pulled in
 * from either layer without creating a cycle.
 */

/** The three ways a project can be shown. `live_url` is the primary "it runs". */
export const PROJECT_TYPE = Object.freeze({
  LIVE_URL: 'live_url',
  FILE: 'file',
  CODE_SNIPPET: 'code_snippet',
});

/** Review lifecycle. Phase 1 only ever produces `draft`. */
export const PROJECT_STATUS = Object.freeze({
  DRAFT: 'draft',
  READY_FOR_REVIEW: 'ready_for_review',
  APPROVED: 'approved',
  CHANGES_REQUESTED: 'changes_requested',
});

/** A project is private until an approved one is explicitly published (Phase 3). */
export const VISIBILITY = Object.freeze({
  PRIVATE: 'private',
  PUBLIC: 'public',
});

export const PROJECT_TYPE_LABEL = Object.freeze({
  [PROJECT_TYPE.LIVE_URL]: 'Live URL',
  [PROJECT_TYPE.FILE]: 'File',
  [PROJECT_TYPE.CODE_SNIPPET]: 'Code',
});

export const PROJECT_STATUS_LABEL = Object.freeze({
  [PROJECT_STATUS.DRAFT]: 'Draft',
  [PROJECT_STATUS.READY_FOR_REVIEW]: 'In review',
  [PROJECT_STATUS.APPROVED]: 'Approved',
  [PROJECT_STATUS.CHANGES_REQUESTED]: 'Changes requested',
});

/** Options for the type picker in the create/edit form. */
export const PROJECT_TYPE_OPTIONS = Object.freeze([
  {
    value: PROJECT_TYPE.LIVE_URL,
    label: 'Live URL',
    hint: 'A deployed project anyone can open and run.',
  },
  {
    value: PROJECT_TYPE.FILE,
    label: 'File',
    hint: 'Upload an image, PDF, or zip of your project.',
  },
  {
    value: PROJECT_TYPE.CODE_SNIPPET,
    label: 'Code snippet',
    hint: 'Paste code — HTML and Python projects run live for visitors.',
  },
]);

/** Field/size limits — enforced in the service sanitizer and mirrored in the UI. */
export const LIMITS = Object.freeze({
  title: 100,
  description: 2000,
  code: 20000,
  tag: 24,
  tags: 8,
  url: 500,
  feedback: 1000, // a review note / thread message
  bio: 240, // public portfolio headline / short bio
  fileBytes: 25 * 1024 * 1024, // 25 MB — matches the app's existing upload cap
  sampleInput: 600, // the student's example answers to their own input() prompts
  dataFileName: 60,
  dataFileContent: 20000, // a bundled data file, stored inline in the doc
  dataFiles: 3,
});

/**
 * A bundled data file's name must be a plain filename: the runner writes these
 * into the sandbox's working directory, so anything path-like ("../main.py")
 * has to be rejected at the storage boundary rather than sanitized on the way
 * out. Letters, digits, dot, dash, underscore — nothing else.
 */
export const DATA_FILE_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/** Non-ambiguous alphabet for the public short code suffix (no 0/o/1/l/i). */
export const SHORTCODE_ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz';

/**
 * Rules for a student-chosen public username (the /p/{username} slug).
 * Lowercase letters, digits and single hyphens; must start with a letter and
 * end alphanumeric — e.g. "arjun", "arjun-codes", "player2".
 */
export const USERNAME = Object.freeze({
  min: 3,
  max: 20,
  pattern: /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/,
});

/** Handles a student may never take (app routes, roles, reserved words). */
export const RESERVED_USERNAMES = Object.freeze([
  'admin', 'administrator', 'api', 'app', 'auth', 'login', 'logout', 'signup',
  'signin', 'student', 'students', 'mentor', 'mentors', 'portfolio', 'p',
  'learnx', 'settings', 'profile', 'about', 'help', 'support', 'contact',
  'root', 'system', 'www', 'me', 'null', 'undefined', 'anonymous',
]);

/** Kinds of entry in a project's lightweight feedback thread. */
export const NOTE_KIND = Object.freeze({
  NOTE: 'note', // a collaborative note (either party, any time)
  CHANGES_REQUESTED: 'changes_requested', // the formal "please revise" message
  APPROVED: 'approved', // the formal approval message
});

/** Accepted upload kinds for a `file` project. */
export const ACCEPTED_FILE_TYPES = Object.freeze([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'application/zip',
  'application/x-zip-compressed',
]);

/** `accept` attribute for the file <input>. */
export const FILE_ACCEPT = 'image/*,application/pdf,.zip';

/** `accept` attribute for the manual thumbnail <input> (images only). */
export const THUMBNAIL_ACCEPT = 'image/png,image/jpeg,image/webp';

/**
 * Common languages offered for a code snippet. Two of them are not just a
 * highlight hint — a snippet in `html` or `python` is RUN, not merely shown
 * (see `RUNNABLE_SNIPPET_LANGUAGES` / `snippetRunKind`), so they lead the list.
 */
export const CODE_LANGUAGES = Object.freeze([
  'html',
  'python',
  'plaintext',
  'javascript',
  'typescript',
  'java',
  'c',
  'cpp',
  'csharp',
  'css',
  'json',
  'bash',
  'sql',
  'go',
  'rust',
]);

/**
 * The snippet languages the preview sandbox can actually EXECUTE, mapped to the
 * "kind" the runner is told to use. Everything else is displayed as source.
 *
 * `python` runs on Pyodide (CPython compiled to WebAssembly) with browser-native
 * stand-ins for tkinter and turtle, so a GUI or a drawing project shows the
 * thing itself rather than its source — which is the whole point for the parent
 * looking at the portfolio. See public/portfolio-python.js.
 */
export const RUNNABLE_SNIPPET_LANGUAGES = Object.freeze({
  html: 'html',
  python: 'python',
});

/** How each runnable kind is described where the project is listed or framed. */
export const RUN_KIND_LABEL = Object.freeze({
  html: 'Interactive web page',
  python: 'Runnable Python app',
});

/** The filename shown on the preview's chrome, per runnable kind. */
export const RUN_KIND_FILENAME = Object.freeze({
  html: 'index.html',
  python: 'main.py',
});

/**
 * Sandbox for live-running a student-authored snippet. Deliberately OMITS
 * `allow-same-origin`: without it the frame gets a unique opaque origin — scripts
 * still run, but with no reach into the parent app, its cookies, or the Firebase
 * auth token in IndexedDB (the CodePen/JSFiddle model). We also withhold
 * `allow-top-navigation`, so the page can't redirect the tab around it.
 *
 * `allow-modals` is here for Python's `messagebox.askyesno()` and
 * `simpledialog.askstring()`: both must return a value to the very next line of
 * Python, so they have to be SYNCHRONOUS — which rules out a styled in-page
 * dialog (that would need an await) and leaves the browser's own prompt/confirm
 * as the only option. (`input()` does NOT use a dialog: the runner collects
 * every prompt and renders them as a form instead.)
 *
 * `allow-downloads` lets a Python project hand back a file it produced (a CSV,
 * a chart, a report). A sandboxed frame's downloads are blocked without it, so
 * the link would look live and do nothing.
 */
export const SNIPPET_SANDBOX = 'allow-scripts allow-popups allow-modals allow-downloads';

/**
 * Standalone runner page that hosts a snippet (see `SnippetFrame`). The snippet
 * is NOT passed as `srcDoc`, because a srcdoc frame inherits the embedder's
 * Content-Security-Policy and our strict CSP would block the snippet's inline and
 * CDN scripts — and, for Python, the Pyodide runtime the runner pulls from a
 * CDN. A real URL carries its own policy instead — the permissive block for this
 * path lives in firebase.json.
 */
export const SNIPPET_SANDBOX_URL = '/portfolio-sandbox.html';
